import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, ChevronLeft, Loader2, Mail, PawPrint } from 'lucide-react'

import { ownerAuth } from '../api/client'
import { ApiError } from '../api/types'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'

const SENT_STATE_KEY = 'owner_login_email_sent'

/**
 * Magic-link email entry. Always shows the same "check your inbox"
 * confirmation regardless of whether the email is known (the backend
 * is the anti-enumeration source of truth, but we mirror its posture
 * client-side so a slow API doesn't leak via UX timing).
 *
 * First-time merchants who haven't claimed via LINE yet will paste
 * their email here, see the success state, but receive no email —
 * which is the design (closes the email self-claim hole). The UI text
 * nudges them back to LINE.
 */
export default function LoginEmail() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  // Tick the resend cooldown.
  useEffect(() => {
    if (cooldown <= 0) return
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000)
    return () => clearTimeout(t)
  }, [cooldown])

  async function submit() {
    if (busy || cooldown > 0) return
    const trimmed = email.trim().toLowerCase()
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) return
    setBusy(true)
    try {
      const res = await ownerAuth.requestMagicLink(trimmed)
      setSent(true)
      setCooldown(res.cooldown_seconds || 60)
      sessionStorage.setItem(SENT_STATE_KEY, trimmed)
    } catch (e) {
      // Even on transport error, surface the same "sent" UI so we don't
      // leak via timing/error oracle. Log the error for ops.
      if (e instanceof ApiError && (e.status === 429 || e.status === 503)) {
        setSent(true)
        setCooldown(60)
      } else {
        // eslint-disable-next-line no-console
        console.warn('[owner-web] magic-link request failed', e)
        setSent(true)
        setCooldown(60)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="container max-w-md py-16">
      <div className="mb-10 flex items-center gap-2 text-base font-semibold">
        <div className="grid size-9 place-items-center rounded-lg bg-primary text-primary-foreground">
          <PawPrint className="size-5" />
        </div>
        <span>{t('brand')}</span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('auth.email.title')}</CardTitle>
          <CardDescription>
            {sent ? t('auth.email.sentSub', { email }) : t('auth.email.sub')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!sent && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="em">{t('auth.email.label')}</Label>
                <Input
                  id="em"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="owner@vet.example"
                  disabled={busy}
                  onKeyDown={(e) => e.key === 'Enter' && submit()}
                />
              </div>
              <Button onClick={submit} disabled={busy || !email.trim()} className="w-full">
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <>
                    <Mail className="size-4" />
                    {t('auth.email.send')}
                    <ArrowRight className="ml-1 size-4" />
                  </>
                )}
              </Button>
            </>
          )}

          {sent && (
            <>
              <div className="rounded-md border border-border bg-secondary/40 px-3 py-4 text-sm text-muted-foreground">
                <p>{t('auth.email.inboxHint')}</p>
                <p className="mt-2 text-xs text-muted-foreground/80">
                  {t('auth.email.sameBrowserHint')}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={submit}
                disabled={cooldown > 0 || busy}
                className="w-full"
              >
                {cooldown > 0
                  ? t('auth.email.resendIn', { seconds: cooldown })
                  : t('auth.email.resend')}
              </Button>
              <p className="text-center text-xs text-muted-foreground/70">
                {t('auth.email.firstTimeHint')}
              </p>
            </>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav('/login')}
            className="w-full text-muted-foreground"
          >
            <ChevronLeft className="size-3.5" />
            {t('auth.email.back')}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
